import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";

type MyPlanResponse = {
  plan?: string;
  status?: string;
  trial_end?: string | null;
  expiry?: string | null;
};

type PlanStatusContextValue = {
  loading: boolean;
  plan: MyPlanResponse | null;
  isInactive: boolean;
  canAccessSidebarRoutes: boolean;
  refresh: () => Promise<void>;
};

const PlanStatusContext = createContext<PlanStatusContextValue | undefined>(undefined);

export function PlanStatusProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<MyPlanResponse | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payment/my-plan/");
      setPlan(data || null);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const value = useMemo<PlanStatusContextValue>(() => {
    const status = String(plan?.status || "").toLowerCase();
    const isInactive = status === "inactive" || status === "canceled";
    return {
      loading,
      plan,
      isInactive,
      canAccessSidebarRoutes: !isInactive,
      refresh: load,
    };
  }, [loading, plan]);

  return <PlanStatusContext.Provider value={value}>{children}</PlanStatusContext.Provider>;
}

export function usePlanStatus() {
  const ctx = useContext(PlanStatusContext);
  if (!ctx) {
    throw new Error("usePlanStatus must be used inside PlanStatusProvider");
  }
  return ctx;
}
